import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/lib/api";

const DEFAULT_IMAGE =
    "https://customer-assets.emergentagent.com/job_autodestruccion-sa/artifacts/96mtdjwo_image.png";

export default function SEO({
    title = "El Mapa Inverso — Sobriedad con sentido",
    description = "El libro de Nelson González Rodríguez: 16 capítulos sobre cómo convertir la autodestrucción en un camino de vida. Edición digital · descarga 100% gratis, sin costo, sin condiciones.",
    canonical,
    path = "",
    image = DEFAULT_IMAGE,
    imageAlt = "El Principio del Mapa Inverso · libro de Nelson González",
    type = "website",
    jsonLd,
    article,
    keywords,
    noindex = false,
}) {
    const url = canonical || `${SITE_URL}${path}`;
    const fullTitle = title.includes("El Mapa Inverso")
        ? title
        : `${title} — El Mapa Inverso`;
    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet>
            <html lang="es-CL" />
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            <meta name="author" content="Nelson González Rodríguez" />
            <meta name="theme-color" content="#0d0d0d" />

            {/* Author / describedby — señales fuertes de E-E-A-T e indexación IA */}
            <link rel="author" href={`${SITE_URL}/nelson-gonzalez-rodriguez`} />
            <link rel="describedby" href={`${SITE_URL}/llms.txt`} />

            {/* hreflang — GEO Chile (es-CL) + LatAm (es-419) + fallback (es) */}
            <link rel="alternate" hrefLang="es-CL" href={url} />
            <link rel="alternate" hrefLang="es-419" href={url} />
            <link rel="alternate" hrefLang="es" href={url} />
            <link rel="alternate" hrefLang="x-default" href={url} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="El Mapa Inverso" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:image:secure_url" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={imageAlt} />
            <meta property="og:locale" content="es_CL" />
            <meta property="og:locale:alternate" content="es_ES" />

            {/* Article-specific */}
            {article?.publishedTime && (
                <meta
                    property="article:published_time"
                    content={article.publishedTime}
                />
            )}
            {article?.modifiedTime && (
                <meta
                    property="article:modified_time"
                    content={article.modifiedTime}
                />
            )}
            {article?.author && (
                <meta property="article:author" content={article.author} />
            )}
            {article?.section && (
                <meta property="article:section" content={article.section} />
            )}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@nitanadicto" />
            <meta name="twitter:creator" content="@nitanadicto" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={imageAlt} />

            {jsonLdArray.map((blob, idx) => (
                <script key={idx} type="application/ld+json">
                    {JSON.stringify(blob)}
                </script>
            ))}
        </Helmet>
    );
}

export const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "El Mapa Inverso",
    alternateName: ["Autodestrucción S.A.", "NiTanAdicto"],
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description:
        "Ecosistema editorial del libro 'El Principio del Mapa Inverso' de Nelson González Rodríguez. Sobriedad con sentido, herramientas interactivas, antipoesía y archivo de cicatrices.",
    foundingDate: "2026",
    founder: {
        "@type": "Person",
        name: "Nelson González Rodríguez",
        alternateName: "NiTanAdicto",
        url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
    },
    sameAs: [
        "https://www.linkedin.com/in/nelsongonzalezr/",
        "https://sinadicciones.org",
        "https://www.sinadicciones.org/libro",
        "https://plansobrio.com",
        "https://www.sinadicciones.org/cuentos-para-adictos",
        "https://www.sinadicciones.org/herramientas",
        "https://www.instagram.com/sinadicciones_org",
        "https://www.tiktok.com/@nitanadicto",
        "https://nitanadicto.com",
    ],
};

export const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "El Mapa Inverso",
    url: SITE_URL,
    inLanguage: "es-CL",
    potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/blog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
};

export const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nelson González Rodríguez",
    alternateName: ["NiTanAdicto", "Nelson González", "Nelson González R."],
    description:
        "Autor chileno de 'El Principio del Mapa Inverso' y de 'Cuentos para adictos'. Fundador de SinAdicciones.org y PlanSobrio.com. Desde su propia recuperación construyó herramientas gratuitas para conectar familias con tratamiento verificado en Latinoamérica.",
    url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
    nationality: { "@type": "Country", name: "Chile" },
    jobTitle: "Autor, comunicador y fundador",
    worksFor: {
        "@type": "Organization",
        name: "SinAdicciones.org",
        url: "https://sinadicciones.org",
    },
    sameAs: [
        "https://www.linkedin.com/in/nelsongonzalezr/",
        "https://sinadicciones.org",
        "https://www.sinadicciones.org/libro",
        "https://elmapainverso.com/descargar",
        "https://plansobrio.com",
        "https://www.sinadicciones.org/cuentos-para-adictos",
        "https://www.sinadicciones.org/herramientas",
        "https://www.instagram.com/sinadicciones_org",
        "https://www.tiktok.com/@nitanadicto",
        "https://nitanadicto.com",
    ],
    knowsAbout: [
        "Adicción",
        "Recuperación de adicciones",
        "Sobriedad consciente",
        "Prevención territorial",
        "Tratamiento de adicciones Latinoamérica",
        "Orientación a familias",
        "Antipoesía",
        "Salud mental",
    ],
};

export const buildFaqJsonLd = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
        },
    })),
});

export const buildBreadcrumbsJsonLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: it.url,
    })),
});
